import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

const TABLE_PARAM = 't';
const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_PAGE_SIZE = 15;

const SORT_KEY = 'sort';
const PAGE_KEY = 'page';
const PAGE_SIZE_KEY = 'pageSize';
const FILTER_PREFIX = 'f.';
const JSON_PREFIX = 'json:';

const tableStateSchema = z.object({
  filters: z.array(z.object({ id: z.string(), value: z.unknown() })).optional(),
  pagination: z
    .object({
      pageIndex: z.number().int().nonnegative().optional(),
      pageSize: z.number().int().positive().optional(),
    })
    .optional(),
  sorting: z.array(z.object({ id: z.string(), desc: z.boolean() })).optional(),
});

export const serializeSorting = (sorting: SortingState): string | undefined => {
  if (sorting.length === 0) return undefined;
  return sorting.map((s) => `${s.id}.${s.desc ? 'desc' : 'asc'}`).join(',');
};

export const deserializeSorting = (value: string | undefined): SortingState | undefined => {
  if (!value) return undefined;
  return value.split(',').map((part) => {
    const lastDot = part.lastIndexOf('.');
    const id = part.slice(0, lastDot);
    const dir = part.slice(lastDot + 1);
    return { id, desc: dir === 'desc' };
  });
};

export const serializePagination = (
  pagination: PaginationState
): { page?: string; pageSize?: string } => {
  const result: { page?: string; pageSize?: string } = {};
  if (pagination.pageIndex !== DEFAULT_PAGE_INDEX) {
    result.page = String(pagination.pageIndex + 1);
  }
  if (pagination.pageSize !== DEFAULT_PAGE_SIZE) {
    result.pageSize = String(pagination.pageSize);
  }
  return result;
};

export const deserializePagination = (
  page: string | undefined,
  pageSize: string | undefined
): Partial<PaginationState> | undefined => {
  const result: Partial<PaginationState> = {};
  let hasValue = false;
  if (page) {
    const parsed = Number.parseInt(page, 10);
    if (!Number.isNaN(parsed) && parsed >= 1) {
      result.pageIndex = parsed - 1;
      hasValue = true;
    }
  }
  if (pageSize) {
    const parsed = Number.parseInt(pageSize, 10);
    if (!Number.isNaN(parsed) && parsed >= 1) {
      result.pageSize = parsed;
      hasValue = true;
    }
  }
  return hasValue ? result : undefined;
};

const serializeFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(String).join('|');
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  return typeof value === 'string' ? value : '';
};

const deserializeFilterValue = (value: string): string | string[] => {
  if (value.includes('|')) return value.split('|');
  return value;
};

export const serializeFilters = (filters: ColumnFiltersState): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const filter of filters) {
    if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
      result[`${FILTER_PREFIX}${filter.id}`] = serializeFilterValue(filter.value);
    }
  }
  return result;
};

export const deserializeFilters = (pairs: Map<string, string>): ColumnFiltersState | undefined => {
  const filters: ColumnFiltersState = [];
  for (const [key, value] of pairs) {
    if (key.startsWith(FILTER_PREFIX) && value) {
      filters.push({
        id: key.slice(FILTER_PREFIX.length),
        value: deserializeFilterValue(value),
      });
    }
  }
  return filters.length > 0 ? filters : undefined;
};

export type UrlTableState = {
  sorting?: SortingState;
  pagination?: Partial<PaginationState>;
  filters?: ColumnFiltersState;
};

const parseStateString = (stateStr: string): Map<string, string> => {
  const pairs = new Map<string, string>();
  if (!stateStr) return pairs;
  for (const part of stateStr.split(';')) {
    const eqIdx = part.indexOf('=');
    if (eqIdx === -1) continue;
    pairs.set(part.slice(0, eqIdx), part.slice(eqIdx + 1));
  }
  return pairs;
};

const serializeStateString = (
  sorting: SortingState,
  pagination: PaginationState,
  filters: ColumnFiltersState
): string | undefined => {
  const state: UrlTableState = {};
  if (sorting.length > 0) state.sorting = sorting;

  const serializedPagination = serializePagination(pagination);
  const parsedPagination = deserializePagination(
    serializedPagination.page,
    serializedPagination.pageSize
  );
  if (parsedPagination) state.pagination = parsedPagination;

  if (filters.length > 0) state.filters = filters;

  return Object.keys(state).length > 0 ? `${JSON_PREFIX}${JSON.stringify(state)}` : undefined;
};

export const parseTableStateFromUrl = (params: URLSearchParams, name: string): UrlTableState => {
  const state: UrlTableState = {};
  const allValues = params.getAll(TABLE_PARAM);
  const prefix = `${name}:`;
  const entry = allValues.find((v) => v.startsWith(prefix));
  if (!entry) return state;

  const stateStr = entry.slice(prefix.length);
  if (stateStr.startsWith(JSON_PREFIX)) {
    try {
      const parsed = tableStateSchema.safeParse(JSON.parse(stateStr.slice(JSON_PREFIX.length)));
      return parsed.success ? parsed.data : state;
    } catch {
      return state;
    }
  }

  // Keep reading links created before typed JSON table state was introduced.
  const pairs = parseStateString(stateStr);

  const sorting = deserializeSorting(pairs.get(SORT_KEY));
  if (sorting) state.sorting = sorting;

  const pagination = deserializePagination(pairs.get(PAGE_KEY), pairs.get(PAGE_SIZE_KEY));
  if (pagination) state.pagination = pagination;

  const filters = deserializeFilters(pairs);
  if (filters) state.filters = filters;

  return state;
};

export const writeTableStateToUrl = (
  params: URLSearchParams,
  name: string,
  sorting: SortingState,
  pagination: PaginationState,
  filters: ColumnFiltersState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const prefix = `${name}:`;

  // Remove existing entry for this table
  const existing = next.getAll(TABLE_PARAM);
  next.delete(TABLE_PARAM);
  for (const val of existing) {
    if (!val.startsWith(prefix)) {
      next.append(TABLE_PARAM, val);
    }
  }

  // Write new entry if state differs from defaults
  const stateStr = serializeStateString(sorting, pagination, filters);
  if (stateStr) {
    next.append(TABLE_PARAM, `${prefix}${stateStr}`);
  }

  return next;
};
